// src/pages/FoodMy.js — 내 음식: 검색 · 목록 · 새 음식 · 수정 · 삭제(숨김)
import React, { useCallback, useEffect, useState } from 'react';
import { foodcustom, foodcustomdelete, foodcustomsearch, foodcustomsearchcount } from '../api/dietApi';
import FoodRow from '../components/ui/FoodRow';
import Pagination from '../components/ui/Pagination';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import CustomFoodForm from './CustomFoodForm';
import './FoodMy.css';

const LIMIT = 10;

export default function FoodMy() {
  const toast = useToast();
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);      // null=닫힘, {}=새 음식, food=수정
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError('');
    const params = query ? { foodNm: query } : {};
    Promise.all([foodcustomsearch({ ...params, offset: (page - 1) * LIMIT, limit: LIMIT }), foodcustomsearchcount(params)])
      .then(([l, c]) => { setItems(Array.isArray(l.data) ? l.data : []); setTotal(Number(c.data) || 0); })
      .catch(() => { setItems([]); setTotal(0); setError('목록을 불러오지 못했습니다.'); })
      .finally(() => setLoading(false));
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      await foodcustom(payload);
      toast(payload.foodCd ? '수정했습니다.' : `${payload.foodNm}을(를) 등록했습니다.`, { type: 'success' });
      setEditing(null);
      if (!payload.foodCd && page !== 1) setPage(1); else load();
    } catch (e) {
      toast(e?.response?.data?.message || '저장 중 오류가 발생했습니다.', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const f = deleting;
    try {
      await foodcustomdelete(f.foodCd);
      toast(`${f.foodNm}을(를) 삭제했습니다.`, { type: 'success' });
      setDeleting(null);
      // 현재 페이지의 마지막 1건을 지웠으면 앞 페이지로
      if (items.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (e) {
      toast(e?.response?.data?.message || '삭제 중 오류가 발생했습니다.', { type: 'error' });
    }
  };

  let body;
  if (loading) body = <p className="fm-msg">불러오는 중…</p>;
  else if (error) body = <p className="fm-msg error">{error}</p>;
  else if (!items.length) {
    body = query
      ? <EmptyState message={`"${query}"에 해당하는 내 음식이 없습니다.`} />
      : <EmptyState message="직접 만든 음식이나 자주 먹는 메뉴를 등록해 두면 식단 기록 때 바로 찾을 수 있어요." actionLabel="첫 음식 등록하기" onAction={() => setEditing({})} />;
  } else {
    body = (
      <>
        {items.map((f) => (
          <FoodRow
            key={f.foodCd}
            food={f}
            meta="100g 기준"
            onClick={() => setEditing(f)}
            right={(
              <button className="icon-btn" type="button" aria-label={`${f.foodNm} 삭제`}
                onClick={(e) => { e.stopPropagation(); setDeleting(f); }}>🗑</button>
            )}
          />
        ))}
        <Pagination page={page} total={total} pageSize={LIMIT} onChange={setPage} />
      </>
    );
  }

  return (
    <div className="fm">
      <div className="fm-head">
        <form className="fm-search" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(keyword.trim()); }}>
          <input className="input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="내 음식 검색" />
          <button className="btn" type="submit">검색</button>
        </form>
        <button className="btn primary" type="button" onClick={() => setEditing({})}>+ 새 음식</button>
      </div>

      {!loading && !error && items.length > 0 && <p className="fm-count">{total.toLocaleString('ko-KR')}개</p>}
      {body}

      <CustomFoodForm open={!!editing} food={editing} saving={saving} onClose={() => setEditing(null)} onSubmit={save} />

      <Modal
        open={!!deleting}
        title="내 음식 삭제"
        size="sm"
        onClose={() => setDeleting(null)}
        footer={(
          <>
            <button className="btn ghost" type="button" onClick={() => setDeleting(null)}>취소</button>
            <button className="btn danger" type="button" onClick={remove}>삭제</button>
          </>
        )}
      >
        <p className="confirm-text">
          <strong>{deleting?.foodNm}</strong>을(를) 삭제할까요?<br />
          목록과 검색에서는 사라지지만, 이미 기록한 식단에는 그대로 남습니다.
        </p>
      </Modal>
    </div>
  );
}
